import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  BarChart3,
  Clock,
  TrendingUp,
  AlertTriangle,
  Activity,
  Calendar,
  AlertCircle,
} from 'lucide-react';
import { format, subDays, parse, startOfDay } from 'date-fns';
import { kanbanApiService } from '../../services/kanbanApi';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { cn } from '../../utils/cn';
import type {
  KanbanMetricsSummary,
  KanbanLeadCycleTimeMetrics,
  KanbanWIPAgingEntry,
  KanbanBottleneckEntry,
  KanbanCFDEntry,
} from '../../types/kanban';
import { PlayerProfile } from '../PlayerProfile';
import { SkeletonLoader } from '../SkeletonLoader';
import { EmptyState } from '../ui/EmptyState';
import { KanbanPriorityBadge } from './KanbanPriorityBadge';

const MS_PER_DAY = 86400000;

function formatDate(ts: number): string {
  return format(ts, 'yyyy-MM-dd');
}

function msToDays(ms: number): string {
  return (ms / MS_PER_DAY).toFixed(1);
}

function formatAge(ms: number): string {
  const totalHours = Math.floor(ms / 3600000);
  const d = Math.floor(totalHours / 24);
  const h = totalHours % 24;
  return `${d}d ${h}h`;
}

function wipAgingRowTone(ageMs: number): string {
  if (ageMs >= 7 * MS_PER_DAY) return 'bg-red-500/5 border-l-2 border-l-red-500/60';
  if (ageMs >= 3 * MS_PER_DAY) return 'bg-yellow-500/5 border-l-2 border-l-yellow-500/50';
  return 'bg-emerald-500/5 border-l-2 border-l-emerald-500/40';
}

function MetricsBarChart({ data }: { data: { label: string; value: number }[] }) {
  const maxVal = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="space-y-2">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-2">
          <span className="text-xs text-glass-400 w-10">{d.label}</span>
          <div className="flex-1 h-5 bg-[var(--surface-inset)] rounded overflow-hidden">
            <div
              className="h-full bg-accent-500/60 rounded"
              style={{ width: `${(d.value / maxVal) * 100}%` }}
            />
          </div>
          <span className="text-xs text-glass-300 w-16 text-right">{(d.value / MS_PER_DAY).toFixed(1)}d</span>
        </div>
      ))}
    </div>
  );
}

function ThroughputChart({ data }: { data: { date: string; count: number }[] }) {
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <span className="text-[10px] text-glass-400">{d.count}</span>
          <div
            className="w-full bg-accent-500/60 rounded-t"
            style={{
              height: `${(d.count / max) * 100}%`,
              minHeight: d.count > 0 ? '4px' : '0',
            }}
          />
          <span className="text-[9px] text-glass-500 -rotate-45 origin-top-left whitespace-nowrap">
            {d.date.slice(5)}
          </span>
        </div>
      ))}
    </div>
  );
}

function leadCycleBarData(m: KanbanLeadCycleTimeMetrics): { label: string; value: number }[] {
  return [
    { label: 'Avg', value: m.avg_ms },
    { label: 'P50', value: m.p50_ms },
    { label: 'P85', value: m.p85_ms },
    { label: 'P95', value: m.p95_ms },
  ];
}

function PercentileBlock({
  title,
  metrics,
}: {
  title: string;
  metrics: KanbanLeadCycleTimeMetrics;
}) {
  return (
    <div className="subcard p-4">
      <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <Clock className="w-4 h-4 text-accent-300 shrink-0" />
        {title}
      </h4>
      <dl className="space-y-1.5 text-sm text-glass-300 mb-4">
        <div className="flex justify-between gap-2">
          <dt>Average</dt>
          <dd className="text-white tabular-nums">{msToDays(metrics.avg_ms)} days</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>P50</dt>
          <dd className="text-white tabular-nums">{msToDays(metrics.p50_ms)} days</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>P85</dt>
          <dd className="text-white tabular-nums">{msToDays(metrics.p85_ms)} days</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>P95</dt>
          <dd className="text-white tabular-nums">{msToDays(metrics.p95_ms)} days</dd>
        </div>
        <div className="flex justify-between gap-2 pt-1 border-t border-glass-200/10">
          <dt>Sample</dt>
          <dd className="text-glass-200 tabular-nums">{metrics.sample_size} cards</dd>
        </div>
      </dl>
      <MetricsBarChart data={leadCycleBarData(metrics)} />
    </div>
  );
}

/** Resolves list column headers preserving first-seen list order */
function cfdPivot(cfd: KanbanCFDEntry[]): {
  dates: string[];
  columns: { listId: string; title: string }[];
  getCount: (date: string, listId: string) => number;
} {
  const dates = [...new Set(cfd.map((e) => e.date))].sort();
  const columns: { listId: string; title: string }[] = [];
  const colIds = new Set<string>();
  for (const e of cfd) {
    if (!colIds.has(e.list_id)) {
      colIds.add(e.list_id);
      columns.push({ listId: e.list_id, title: e.list_title });
    }
  }
  const key = (d: string, lid: string) => `${d}|${lid}`;
  const countMap = new Map<string, number>();
  for (const e of cfd) {
    countMap.set(key(e.date, e.list_id), e.card_count);
  }
  return {
    dates,
    columns,
    getCount: (date, listId) => countMap.get(key(date, listId)) ?? 0,
  };
}

export interface KanbanMetricsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  boardTitle: string;
}

export const KanbanMetricsPanel = ({ isOpen, onClose, boardId, boardTitle }: KanbanMetricsPanelProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const defaultRange = useMemo(() => {
    const to = startOfDay(Date.now()).getTime();
    const from = subDays(to, 30).getTime();
    return { from, to };
  }, []);

  const [metrics, setMetrics] = useState<KanbanMetricsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ from: number; to: number }>(defaultRange);
  const [boardCreatorId, setBoardCreatorId] = useState<string | null>(null);

  const loadMetrics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fromStr = formatDate(dateRange.from);
      const toStr = formatDate(dateRange.to);
      // Try the combined summary endpoint first (most efficient)
      let data: KanbanMetricsSummary;
      try {
        data = await kanbanApiService.getMetricsSummary(boardId, fromStr, toStr);
      } catch {
        // Fallback: load individual metric endpoints if summary fails
        const [lt, ct, tp, wa, bn, cf] = await Promise.all([
          kanbanApiService.getLeadTime(boardId, dateRange.from, dateRange.to).catch(() => ({ avg_ms: 0, p50_ms: 0, p85_ms: 0, p95_ms: 0, sample_size: 0 })),
          kanbanApiService.getCycleTime(boardId, dateRange.from, dateRange.to).catch(() => ({ avg_ms: 0, p50_ms: 0, p85_ms: 0, p95_ms: 0, sample_size: 0 })),
          kanbanApiService.getThroughput(boardId, dateRange.from, dateRange.to).catch(() => ({ granularity: 'week' as const, data: [], total: 0 })),
          kanbanApiService.getWIPAging(boardId).catch(() => []),
          kanbanApiService.getBottlenecks(boardId).catch(() => []),
          kanbanApiService.getCFD(boardId, fromStr, toStr).catch(() => []),
        ]);
        data = { leadTime: lt, cycleTime: ct, throughput: tp, wipAging: wa, bottlenecks: bn, cfd: cf };
      }
      setMetrics(data);
    } catch (e) {
      setMetrics(null);
      setError(e instanceof Error ? e.message : 'Failed to load metrics.');
    } finally {
      setIsLoading(false);
    }
  }, [boardId, dateRange.from, dateRange.to]);

  useEffect(() => {
    if (!isOpen) return;
    void loadMetrics();
  }, [isOpen, loadMetrics]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    void kanbanApiService
      .getBoard(boardId)
      .then((b) => {
        if (!cancelled) setBoardCreatorId(b.created_by);
      })
      .catch(() => {
        if (!cancelled) setBoardCreatorId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, boardId]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    setDateRange(defaultRange);
    setMetrics(null);
    setError(null);
  }, [isOpen, boardId, defaultRange]);

  const sortedWipAging = useMemo(() => {
    if (!metrics?.wipAging) return [];
    return [...metrics.wipAging].sort((a, b) => b.age_ms - a.age_ms);
  }, [metrics]);

  const hasMeaningfulMetrics = useMemo(() => {
    if (!metrics) return false;
    return (
      metrics.leadTime.sample_size > 0 ||
      metrics.cycleTime.sample_size > 0 ||
      metrics.throughput.total > 0 ||
      metrics.wipAging.length > 0 ||
      metrics.bottlenecks.length > 0 ||
      metrics.cfd.length > 0
    );
  }, [metrics]);

  const cfdView = useMemo(() => {
    if (!metrics?.cfd.length) return null;
    return cfdPivot(metrics.cfd);
  }, [metrics]);

  if (!isOpen) return null;

  const fromInputValue = formatDate(dateRange.from);
  const toInputValue = formatDate(dateRange.to);

  const modalContent = (
    <div className="modal-overlay z-[9999] md:p-6">
      <div
        ref={modalRef}
        className={cn('modal-panel max-w-5xl !p-0 max-h-[92vh] overflow-hidden')}
      >
        <header className="shrink-0 border-b border-glass-200/10 p-4 md:p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="modal-title flex items-center gap-2 pr-2">
                <BarChart3 className="w-5 h-5 text-accent-400 shrink-0" />
                <span className="truncate">Board Metrics — {boardTitle}</span>
              </h2>
              {boardCreatorId && (
                <div className="mt-2 flex items-center gap-2 text-xs text-glass-400">
                  <span>Created by</span>
                  <PlayerProfile uuid={boardCreatorId} inline showUuid={false} showAvatar />
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="btn-icon shrink-0"
              aria-label="Close"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1.5 text-xs font-medium text-glass-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                From
              </span>
              <input
                type="date"
                value={fromInputValue}
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  setDateRange((r) => ({ ...r, from: startOfDay(parse(v, 'yyyy-MM-dd', new Date())).getTime() }));
                }}
                className="glass-input"
              />
            </label>
            <label className="flex flex-col gap-1.5 text-xs font-medium text-glass-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                To
              </span>
              <input
                type="date"
                value={toInputValue}
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  setDateRange((r) => ({ ...r, to: startOfDay(parse(v, 'yyyy-MM-dd', new Date())).getTime() }));
                }}
                className="glass-input"
              />
            </label>
            <button
              type="button"
              onClick={() => void loadMetrics()}
              disabled={isLoading || dateRange.from > dateRange.to}
              className="glass-button"
            >
              Apply range
            </button>
          </div>
        </header>

        <div className="modal-body !mx-0 !px-4 md:!px-5 py-4 md:py-5 space-y-6">
          {isLoading && (
            <div className="space-y-6" aria-busy="true" aria-label="Loading metrics">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="subcard p-4 space-y-2">
                    <SkeletonLoader className="h-3 w-24" />
                    <SkeletonLoader className="h-7 w-16" />
                  </div>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {[1, 2].map((i) => (
                  <div key={i} className="subcard p-4 space-y-3">
                    <SkeletonLoader className="h-4 w-28" />
                    <SkeletonLoader className="h-3 w-full" />
                    <SkeletonLoader className="h-3 w-5/6" />
                    <SkeletonLoader className="h-3 w-2/3" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isLoading && error && (
            <EmptyState
              bare
              tone="danger"
              icon={AlertCircle}
              title="Failed to load metrics"
              description={error}
              action={
                <button type="button" onClick={() => void loadMetrics()} className="glass-button">
                  Retry
                </button>
              }
            />
          )}

          {!isLoading && !error && metrics && !hasMeaningfulMetrics && (
            <EmptyState
              bare
              icon={Activity}
              title="Not enough data to compute metrics"
              description="Widen the date range or come back once cards have moved through the board."
            />
          )}

          {!isLoading && !error && metrics && hasMeaningfulMetrics && (
            <>
              <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="subcard p-4">
                  <div className="eyebrow flex items-center gap-1.5 mb-1">
                    <Clock className="w-3.5 h-3.5" />
                    Median Lead Time
                  </div>
                  <p className="text-2xl font-semibold tracking-tight text-white tabular-nums">{msToDays(metrics.leadTime.p50_ms)} days</p>
                </div>
                <div className="subcard p-4">
                  <div className="eyebrow flex items-center gap-1.5 mb-1">
                    <TrendingUp className="w-3.5 h-3.5" />
                    Median Cycle Time
                  </div>
                  <p className="text-2xl font-semibold tracking-tight text-white tabular-nums">{msToDays(metrics.cycleTime.p50_ms)} days</p>
                </div>
                <div className="subcard p-4">
                  <div className="eyebrow flex items-center gap-1.5 mb-1">
                    <BarChart3 className="w-3.5 h-3.5" />
                    Throughput
                  </div>
                  <p className="text-2xl font-semibold tracking-tight text-white tabular-nums">{metrics.throughput.total}</p>
                  <p className="text-[11px] text-glass-500 mt-0.5">cards delivered in period</p>
                </div>
                <div className="subcard p-4">
                  <div className="eyebrow flex items-center gap-1.5 mb-1">
                    <Activity className="w-3.5 h-3.5" />
                    Sample Size
                  </div>
                  <p className="text-2xl font-semibold tracking-tight text-white tabular-nums">{metrics.leadTime.sample_size}</p>
                  <p className="text-[11px] text-glass-500 mt-0.5">lead time sample</p>
                </div>
              </section>

              <section className="grid md:grid-cols-2 gap-4">
                <PercentileBlock title="Lead Time" metrics={metrics.leadTime} />
                <PercentileBlock title="Cycle Time" metrics={metrics.cycleTime} />
              </section>

              <section className="subcard p-4 md:p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-accent-400" />
                  Throughput
                </h3>
                {metrics.throughput.data.length > 0 ? (
                  <ThroughputChart data={metrics.throughput.data} />
                ) : (
                  <p className="text-sm text-glass-400">No throughput data points for this range.</p>
                )}
              </section>

              <section className="subcard p-4 md:p-5 overflow-x-auto">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-accent-400" />
                  WIP Aging
                </h3>
                {sortedWipAging.length === 0 ? (
                  <p className="text-sm text-glass-400">No cards in progress for aging analysis.</p>
                ) : (
                  <table className="table min-w-[640px]">
                    <thead>
                      <tr>
                        <th className="th !px-2">Card</th>
                        <th className="th !px-2">List</th>
                        <th className="th !px-2">Age</th>
                        <th className="th !px-2">Priority</th>
                        <th className="th !px-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedWipAging.map((row: KanbanWIPAgingEntry) => (
                        <tr
                          key={row.card_id}
                          className={cn('border-b border-glass-200/5', wipAgingRowTone(row.age_ms))}
                        >
                          <td className="td !px-2 !py-2 text-white font-medium max-w-[220px] truncate" title={row.title}>
                            {row.title}
                          </td>
                          <td className="td !px-2 !py-2 text-glass-300">{row.list_title}</td>
                          <td className="td !px-2 !py-2 text-glass-200 tabular-nums whitespace-nowrap">{formatAge(row.age_ms)}</td>
                          <td className="td !px-2 !py-2">
                            <KanbanPriorityBadge priority={row.priority} />
                          </td>
                          <td className="td !px-2 !py-2">
                            {row.is_blocked ? (
                              <span className="inline-flex items-center gap-1 text-xs text-red-300">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                Blocked
                              </span>
                            ) : (
                              <span className="text-glass-500 text-xs">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="subcard p-4 md:p-5 overflow-x-auto">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-accent-400" />
                  Bottleneck Analysis
                </h3>
                {metrics.bottlenecks.length === 0 ? (
                  <p className="text-sm text-glass-400">No bottleneck data.</p>
                ) : (
                  <table className="table min-w-[560px]">
                    <thead>
                      <tr>
                        <th className="th !px-2">List</th>
                        <th className="th !px-2">Cards</th>
                        <th className="th !px-2">WIP limit</th>
                        <th className="th !px-2">Limit</th>
                        <th className="th !px-2">Avg age</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.bottlenecks.map((b: KanbanBottleneckEntry) => (
                        <tr
                          key={b.list_id}
                          className={cn(
                            'border-b border-glass-200/5',
                            b.is_over_limit && 'bg-red-500/5',
                          )}
                        >
                          <td className="td !px-2 !py-2 text-white font-medium">{b.list_title}</td>
                          <td className="td !px-2 !py-2 text-glass-200 tabular-nums">{b.card_count}</td>
                          <td className="td !px-2 !py-2 text-glass-300 tabular-nums">
                            {b.wip_limit == null ? '—' : b.wip_limit}
                          </td>
                          <td className="td !px-2 !py-2">
                            {b.is_over_limit ? (
                              <span className="badge badge-danger">
                                Over limit
                              </span>
                            ) : (
                              <span className="text-glass-500 text-xs">OK</span>
                            )}
                          </td>
                          <td className="td !px-2 !py-2 text-glass-200 tabular-nums">{msToDays(b.avg_wip_age_ms)} days</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              {cfdView && cfdView.dates.length > 0 && (
                <section className="subcard p-4 md:p-5 space-y-4 overflow-x-auto">
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <Activity className="w-4 h-4 text-accent-400" />
                    Cumulative Flow (CFD)
                  </h3>
                  <p className="text-xs text-glass-400">Card counts by list over the selected period.</p>
                  <div className="space-y-3 min-w-[480px]">
                    {cfdView.dates.map((date) => {
                      const rowTotal = cfdView.columns.reduce((s, c) => s + cfdView.getCount(date, c.listId), 0);
                      return (
                        <div key={date} className="space-y-1">
                          <div className="flex justify-between text-xs text-glass-400">
                            <span className="font-mono">{date}</span>
                            <span className="text-glass-500">Total {rowTotal}</span>
                          </div>
                          <div className="flex h-6 rounded overflow-hidden border border-glass-200/10">
                            {cfdView.columns.map((col) => {
                              const count = cfdView.getCount(date, col.listId);
                              const pct = rowTotal > 0 ? (count / rowTotal) * 100 : 0;
                              return (
                                <div
                                  key={`${date}-${col.listId}`}
                                  className="h-full bg-accent-500/50 border-r border-glass-900/30 last:border-r-0 flex items-center justify-center min-w-0"
                                  style={{ width: `${pct}%` }}
                                  title={`${col.title}: ${count}`}
                                >
                                  {pct > 8 && (
                                    <span className="text-[10px] text-white/90 truncate px-0.5">{count}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-2 text-[11px] text-glass-400">
                    {cfdView.columns.map((col) => (
                      <span key={col.listId} className="inline-flex items-center gap-1">
                        <span className="w-2 h-2 rounded-sm bg-accent-500/60 shrink-0" />
                        {col.title}
                      </span>
                    ))}
                  </div>
                  <div className="subcard overflow-x-auto">
                    <table className="table text-xs">
                      <thead>
                        <tr>
                          <th className="th !px-2">Date</th>
                          {cfdView.columns.map((col) => (
                            <th
                              key={col.listId}
                              className="th !px-2 text-right max-w-[120px] truncate"
                              title={col.title}
                            >
                              {col.title}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {cfdView.dates.map((date) => (
                          <tr key={date}>
                            <td className="td !py-2 !px-2 text-glass-400 font-mono whitespace-nowrap">{date}</td>
                            {cfdView.columns.map((col) => (
                              <td key={col.listId} className="td !px-2 !py-2 text-right text-glass-200 tabular-nums">
                                {cfdView.getCount(date, col.listId)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
